import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';
import '../../assets/fonts/satoshi.css';

// Forest Theme - Elegant nature-inspired design
const palette = {
  mode: 'light',
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: {
    light: '#81C784', // Light forest green
    main: '#2E7D32', // Rich forest green
    dark: '#1B5E20', // Deep forest green
    text: '#2E7D32',
    contrastText: '#FFFFFF',
  },
  secondary: {
    light: '#A5D6A7', // Soft sage
    main: '#66BB6A', // Fresh leaf green
    dark: '#43A047', // Deep sage
    contrastText: '#FFFFFF',
  },
  success: {
    light: '#C8E6C9',
    main: '#4CAF50', // Natural green
    dark: '#388E3C',
    contrastText: '#FFFFFF',
  },
  warning: {
    light: '#FFF3E0',
    main: '#D4AC0D', // Warm amber
    dark: '#B7950B',
    contrastText: '#FFFFFF',
  },
  error: {
    light: '#FFCDD2',
    main: '#E57373', // Soft red
    dark: '#D32F2F',
    contrastText: '#FFFFFF',
  },
  info: {
    light: '#B3E5FC',
    main: '#29B6F6', // Fresh blue
    dark: '#0288D1',
    contrastText: '#FFFFFF',
  },
  neutral: {
    light: '#E8F5E9',
    main: '#81C784',
    dark: '#2E7D32',
    contrastText: '#FFFFFF',
  },
  appointment: {
    main: '#4CAF50',
  },
  patient: {
    main: '#2E7D32',
  },
  alert: {
    main: '#E57373',
  },
  background: {
    default: '#F1F8E9', // Light forest mist
    paper: '#FFFFFF',
    subtle: '#E8F5E9', // Soft green tint
  },
  divider: 'rgba(46, 125, 50, 0.12)',
  text: {
    primary: '#1B5E20', // Deep forest green
    secondary: '#2E7D32', // Rich forest green
    disabled: '#A5D6A7', // Soft sage
  },
};

// Typography with natural elegance
const typography = {
  fontFamily: "'Satoshi', sans-serif",
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { 
    fontSize: '2.5rem', 
    fontWeight: 700, 
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h2: { 
    fontSize: '2rem', 
    fontWeight: 700, 
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h3: { 
    fontSize: '1.5rem', 
    fontWeight: 600, 
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: { 
    fontSize: '1.25rem', 
    fontWeight: 600, 
    lineHeight: 1.4
  },
  h5: { 
    fontSize: '1.125rem', 
    fontWeight: 600, 
    lineHeight: 1.5
  },
  h6: { 
    fontSize: '1rem', 
    fontWeight: 600, 
    lineHeight: 1.5
  },
  subtitle1: { 
    fontSize: '1rem', 
    fontWeight: 500,
    lineHeight: 1.5,
    color: palette.text.secondary,
  },
  subtitle2: { 
    fontSize: '0.875rem', 
    fontWeight: 500,
    lineHeight: 1.57,
    color: palette.text.secondary,
  },
  body1: { 
    fontSize: '1rem', 
    lineHeight: 1.6,
    letterSpacing: '0.01em',
  },
  body2: { 
    fontSize: '0.875rem', 
    fontWeight: 300,
    lineHeight: 1.57,
    letterSpacing: '0.01em',
  },
  button: { 
    fontSize: '0.875rem', 
    fontWeight: 600, 
    letterSpacing: '0.02em',
    textTransform: 'none',
  },
  caption: { 
    fontSize: '0.75rem', 
    lineHeight: 1.5,
    color: palette.text.secondary,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    lineHeight: 1.6,
    textTransform: 'uppercase',
  },
};

const shape = { borderRadius: 12 };
const spacing = 8;

// Forest-inspired shadows with natural depth
const shadows = [
  'none',
  '0px 1px 2px rgba(27, 94, 32, 0.06)',
  '0px 2px 4px rgba(27, 94, 32, 0.08)',
  '0px 4px 8px rgba(27, 94, 32, 0.12)',
  '0px 8px 16px rgba(27, 94, 32, 0.08)',
  '0px 12px 24px rgba(27, 94, 32, 0.12)',
  '0px 14px 28px rgba(27, 94, 32, 0.15)',
  '0px 16px 32px rgba(27, 94, 32, 0.18)',
  '0px 20px 40px rgba(27, 94, 32, 0.20)',
  '0px 24px 48px rgba(27, 94, 32, 0.25)',
  '0px 28px 56px rgba(27, 94, 32, 0.30)',
  '0px 32px 64px rgba(27, 94, 32, 0.35)',
  '0px 36px 72px rgba(27, 94, 32, 0.40)',
  '0px 40px 80px rgba(27, 94, 32, 0.45)',
  '0px 44px 88px rgba(27, 94, 32, 0.50)',
  '0px 48px 96px rgba(27, 94, 32, 0.55)',
  '0px 52px 104px rgba(27, 94, 32, 0.60)',
  '0px 56px 112px rgba(27, 94, 32, 0.65)',
  '0px 60px 120px rgba(27, 94, 32, 0.70)',
  '0px 64px 128px rgba(27, 94, 32, 0.75)',
  '0px 68px 136px rgba(27, 94, 32, 0.80)',
  '0px 72px 144px rgba(27, 94, 32, 0.85)',
  '0px 76px 152px rgba(27, 94, 32, 0.90)',
  '0px 80px 160px rgba(27, 94, 32, 0.95)',
  '0px 84px 168px rgba(27, 94, 32, 1.0)',
];

// Component overrides with forest aesthetics
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      contained: {
        boxShadow: 'none',
        background: `linear-gradient(135deg, ${alpha(palette.primary.main, 1)} , ${alpha(palette.primary.main, 1)} )`,
        backdropFilter: 'blur(10px)', 
        borderRadius: '12px',
        WebkitBackdropFilter: 'blur(10px)',
        color: palette.common.white,
        border: `1px solid ${alpha(palette.common.white, 0.2)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.2)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          boxShadow: 'none',
          background: `linear-gradient(135deg, ${alpha(palette.primary.main, 1)} 20%, ${alpha(palette.secondary.main, 1)} 100%)`,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        },
      },
      outlined: {
        borderWidth: '1.5px',
        borderColor: alpha(palette.primary.main, 0.3),
        background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, ${alpha(palette.common.white, 0.05)} 100%)`,
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          borderWidth: '1.5px',
          borderColor: alpha(palette.primary.main, 0.5),
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.15)} 0%, ${alpha(palette.common.white, 0.1)} 100%)`,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        },
      },
      text: {
        '&:hover': {
          background: alpha(palette.primary.main, 0.04),
        },
      },
      sizeSmall: {
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
      sizeMedium: {
        padding: '6px 16px',
        fontSize: '0.875rem',
      },
      sizeLarge: {
        padding: '8px 20px',
        fontSize: '0.9375rem',
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 3,
        boxShadow: shadows[3],
        background: `linear-gradient(135deg, ${alpha(palette.primary.main, 0.1)} 20%, ${alpha(palette.secondary.light, 0.1)} 100%)`,
        backdropFilter: 'blur(5px)',
        border: `1px solid ${alpha(palette.common.white, 0.1)}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        borderRadius: `24px 24px 24px 24px`,
        WebkitBackdropFilter: 'blur(20px)', // For Safari support
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          boxShadow: shadows[6],
          transform: 'translateY(-2px)',
          background: `linear-gradient(145deg, ${alpha(palette.primary.light, 0.1)} 20%, ${alpha(palette.secondary.light, 0.1)} 100%)`,
        },
      }
    }
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '24px',
        position: 'relative',
        zIndex: 1
      },
      title: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      subheader: {
        fontSize: '0.875rem',
        color: alpha(palette.text.secondary, 0.9),
      }
    }
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '24px',
        position: 'relative',
        zIndex: 1,
        '&:last-child': {
          paddingBottom: '24px',
        }
      }
    }
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
        justifyContent: 'flex-end',
        position: 'relative',
        zIndex: 1
      }
    }
  }
};

let theme = createTheme({ palette, typography, shape, spacing, shadows, components });
theme = responsiveFontSizes(theme);

export default theme; 